import { PayloadAction } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { NotifyService } from 'helpers';
import {
  CREATE_PUBLIC_HOLIDAY_PAYLOAD,
  PUBLIC_HOLIDAY_DETAIL_TYPE,
  PUBLIC_HOLIDAY_LIST_PARAMS_REQUEST,
  PUBLIC_HOLIDAY_LIST_STATE,
  UPDATE_PUBLIC_HOLIDAY_PAYLOAD,
} from 'models/public-holiday';
import { call, put, takeLatest } from 'redux-saga/effects';
import {
  createPublicHolidayApi,
  deletePublicHolidayApi,
  getDetailPublicHolidayApi,
  getPublicHolidayListApi,
  updatePublicHolidayApi,
} from 'services/public-holidays';
import { setGlobalLoading } from 'store/reducers/global';
import {
  setPublicHolidayDetail,
  setPublicHolidayListSuccess,
} from 'store/reducers/public-holiday';
import {
  CREATE_PUBLIC_HOLIDAY_LIST_REQUEST,
  DELETE_PUBLIC_HOLIDAY_REQUEST,
  GET_PUBLIC_HOLIDAY_DETAIL_REQUEST,
  GET_PUBLIC_HOLIDAY_LIST_REQUEST,
  UPDATE_PUBLIC_HOLIDAY_LIST_REQUEST,
} from 'store/reducers/public-holiday/actionType';

function* getPublicHolidayListRequest(
  action: PayloadAction<{
    params?: PUBLIC_HOLIDAY_LIST_PARAMS_REQUEST;
  }>
) {
  try {
    yield put(setGlobalLoading(true));
    const response: AxiosResponse<{ data: PUBLIC_HOLIDAY_LIST_STATE }> =
      yield call(getPublicHolidayListApi, action.payload.params);
    yield put(
      setPublicHolidayListSuccess({
        records: response.data.data.records,
        metadata: response.data.data.metadata,
      })
    );
    yield put(setGlobalLoading(false));
  } catch (error) {
    NotifyService.error(error);
    yield put(setGlobalLoading(false));
  }
}

function* getPublicHolidayDetailRequest(
  action: PayloadAction<{
    id: string;
  }>
) {
  try {
    yield put(setGlobalLoading(true));
    const response: AxiosResponse<{ data: PUBLIC_HOLIDAY_DETAIL_TYPE }> =
      yield call(getDetailPublicHolidayApi, action.payload.id);
    yield put(setPublicHolidayDetail(response.data.data));
    yield put(setGlobalLoading(false));
  } catch (error) {
    NotifyService.error(error);
    yield put(setGlobalLoading(false));
  }
}

function* createPublicHolidayListRequest(
  action: PayloadAction<{
    data?: CREATE_PUBLIC_HOLIDAY_PAYLOAD;
  }>
) {
  try {
    yield put(setGlobalLoading(true));
    yield call(
      createPublicHolidayApi,
      action.payload.data as CREATE_PUBLIC_HOLIDAY_PAYLOAD
    );
    NotifyService.success('Created successfully');
  } catch (error) {
    NotifyService.error(error);
    yield put(setGlobalLoading(false));
  } finally {
    yield put(setGlobalLoading(false));
  }
}

function* updatePublicHolidayListRequest(
  action: PayloadAction<{
    data?: UPDATE_PUBLIC_HOLIDAY_PAYLOAD & { id: string };
  }>
) {
  try {
    yield put(setGlobalLoading(true));
    yield call(
      updatePublicHolidayApi,
      action.payload.data?.id as string,
      action.payload.data as UPDATE_PUBLIC_HOLIDAY_PAYLOAD
    );
    NotifyService.success('Updated successfully');
  } catch (error) {
    NotifyService.error(error);
    yield put(setGlobalLoading(false));
  } finally {
    yield put(setGlobalLoading(false));
  }
}

function* deletePublicHolidayRequest(
  action: PayloadAction<{
    id: string;
    onSuccess?: () => void;
  }>
) {
  try {
    yield put(setGlobalLoading(true));
    yield call(deletePublicHolidayApi, action.payload.id);
    NotifyService.success('Deleted successfully');
    if (action.payload.onSuccess) action.payload.onSuccess();
  } catch (error: unknown) {
    NotifyService.error(error);
    yield put(setGlobalLoading(false));
  } finally {
    yield put(setGlobalLoading(false));
  }
}

function* publicHolidays() {
  yield takeLatest(
    GET_PUBLIC_HOLIDAY_LIST_REQUEST,
    getPublicHolidayListRequest
  );
  yield takeLatest(DELETE_PUBLIC_HOLIDAY_REQUEST, deletePublicHolidayRequest);
  yield takeLatest(
    CREATE_PUBLIC_HOLIDAY_LIST_REQUEST,
    createPublicHolidayListRequest
  );
  yield takeLatest(
    UPDATE_PUBLIC_HOLIDAY_LIST_REQUEST,
    updatePublicHolidayListRequest
  );
  yield takeLatest(
    GET_PUBLIC_HOLIDAY_DETAIL_REQUEST,
    getPublicHolidayDetailRequest
  );
}

export default publicHolidays;
